import React, { useState, useEffect } from "react";
import JSONDisplay from "./JSONDisplay";
import ImageDisplay from "./ImageDisplay";
import { getPageData, getPageImage } from "../../../utils/Api";

type DisplayPredictionsProps = {
  pageID: string | null;
};

type PageData = {
  raw_data: any;
  readable_data: any;
};

const DisplayPredictions: React.FC<DisplayPredictionsProps> = ({ pageID }) => {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [rawData, setRawData] = useState<any>({});
  const [readableData, setReadableData] = useState<any>({});

  useEffect(() => {
    if (!imageSrc) {
      setLoadedImage(null);
      return;
    }

    const img = new Image();
    img.onload = () => setLoadedImage(img);
    img.src = imageSrc;

    return () => {
      img.onload = null;
    };
  }, [imageSrc]);

  useEffect(() => {
    if (!pageID) return;

    const fetchImage = async () => {
      try {
        const { data } = await getPageImage(pageID);
        setImageSrc(data.image);
      } catch (error) {
        console.error("Error fetching image:", error);
      }
    };

    fetchImage();
  }, [pageID]);

  useEffect(() => {
    if (!pageID) return;

    const fetchJson = async () => {
      try {
        const { status, data }: { status: number; data: PageData } =
          await getPageData(pageID);
        if (status === 206) {
          console.info("Results not ready...");
          setRawData({});
          setReadableData({});
        } else {
          setRawData(data.raw_data);
          setReadableData(data.readable_data);
        }
      } catch (error: any) {
        console.error(`Error: ${error.message}`);
      }
    };

    fetchJson();
  }, [pageID]);

  return (
    <div className="h-full w-full relative">
      <ImageDisplay data={rawData} image={loadedImage} />
      <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-white/70 backdrop-blur-sm p-4 border-t border-slate-200 shadow-2xl">
        <JSONDisplay data={readableData} />
      </div>
    </div>
  );
};

export default DisplayPredictions;
